import { computed, effect, Injectable, signal, type Signal } from '@angular/core';

import { INVERTED_LEGACY_KEYS, LEGACY_KEY_MAP, STORAGE_KEY } from './user-config.model';

@Injectable({ providedIn: 'root' })
export class UserConfigService {
  #config = signal<Record<string, unknown>>(this.#load());

  constructor() {
    effect(() => {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(this.#config()));
    });
  }

  get<T>(key: string, defaultValue: T): Signal<T> {
    return computed(() => (this.#config()[key] as T) ?? defaultValue);
  }

  set(key: string, value: unknown): void {
    this.#config.update((current) => ({ ...current, [key]: value }));
  }

  #load(): Record<string, unknown> {
    const stored = localStorage.getItem(STORAGE_KEY);

    if (stored) {
      try {
        return JSON.parse(stored) as Record<string, unknown>;
      } catch {
        return {};
      }
    }

    return this.#migrateLegacyKeys();
  }

  // TODO: Remove legacy key migration once all active users have been migrated - SHB-19574
  #migrateLegacyKeys(): Record<string, unknown> {
    const config: Record<string, unknown> = {};

    for (const [legacyKey, configKey] of Object.entries(LEGACY_KEY_MAP)) {
      const raw = localStorage.getItem(legacyKey);

      if (raw === null) {
        continue;
      }

      const parsed = configKey === 'employeeListGroupBy' ? raw : this.#parseBool(raw);
      const value = INVERTED_LEGACY_KEYS.has(legacyKey) ? !parsed : parsed;

      config[configKey] = value;
      localStorage.removeItem(legacyKey);
    }

    return config;
  }

  #parseBool(value: string): boolean {
    try {
      return !!JSON.parse(value);
    } catch {
      return value === 'true';
    }
  }
}
